import type { ReportQueryResponse } from '@/api/types';
import { calendarDayRange } from '@/lib/datetime';
import {
  reportingPublishedDays,
  reportingZeroFillDays,
} from '@/lib/reporting-coverage';
import {
  emptyPerfHaloMetrics,
  hasPerfHaloAttributionEvidence,
  hasPerfHaloDeliveryEvidence,
  perfHaloTicketValueDollars,
  perfHaloTickets,
  projectPerfHaloMetrics,
  sumPerfHaloMetrics,
} from './perf-halo-metrics';

export interface PerfHaloTrendPoint {
  date: string;
  deliveryAvailable: boolean;
  attributionAvailable: boolean;
  grossSpendCents: number;
  attributionGrossSpendCents: number | null;
  impressions: number;
  clicks: number;
  totalTickets: number;
  directTickets: number;
  haloTickets: number;
  totalTicketValueCents: number;
  directTicketValueCents: number;
  haloTicketValueCents: number;
}

/**
 * Calendar-align a complete daily query. Published zeroes remain zero while an
 * unpublished day has unavailable delivery and/or attribution, producing a
 * chart gap instead of a fabricated zero.
 */
export function buildPerfHaloTrendPoints(
  response: ReportQueryResponse | undefined,
  from: string,
  to: string,
): PerfHaloTrendPoint[] {
  if (!response) return [];
  const byDay = new Map<string, ReturnType<typeof projectPerfHaloMetrics>[]>();
  for (const row of response.rows) {
    const day = row.dimensions.day;
    if (!day) continue;
    const rows = byDay.get(day) ?? [];
    rows.push(projectPerfHaloMetrics(row.metrics));
    byDay.set(day, rows);
  }
  const deliveryCovered = reportingZeroFillDays(response.freshness, 'delivery');
  const deliveryPublished = reportingPublishedDays(response.freshness, 'delivery');
  const attributionPublished = reportingPublishedDays(response.freshness, 'attribution');

  return calendarDayRange(from, to).map((date) => {
    const dailyRows = byDay.get(date);
    const metrics = dailyRows
      ? sumPerfHaloMetrics(dailyRows)
      : emptyPerfHaloMetrics();
    const deliveryAvailable = deliveryPublished.has(date)
      || deliveryCovered.has(date)
      || hasPerfHaloDeliveryEvidence(metrics);
    const attributionAvailable = attributionPublished.has(date)
      || hasPerfHaloAttributionEvidence(metrics);
    return {
      date,
      deliveryAvailable,
      attributionAvailable,
      grossSpendCents: metrics.gross_spend_cents,
      attributionGrossSpendCents: attributionAvailable
        ? metrics.attribution_gross_spend_cents
        : null,
      impressions: metrics.impressions,
      clicks: metrics.clicks,
      totalTickets: perfHaloTickets(metrics, 'total'),
      directTickets: perfHaloTickets(metrics, 'direct'),
      haloTickets: perfHaloTickets(metrics, 'halo'),
      totalTicketValueCents: perfHaloTicketValueDollars(metrics, 'total') * 100,
      directTicketValueCents: perfHaloTicketValueDollars(metrics, 'direct') * 100,
      haloTicketValueCents: perfHaloTicketValueDollars(metrics, 'halo') * 100,
    };
  });
}
